import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  Stack,
  Divider,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  alpha,
  useTheme,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Campaign as CampaignIcon,
  Store as StoreIcon,
  ConfirmationNumber as CouponIcon,
  CheckCircle,
  Info,
  Person,
  Policy,
  Search,
} from '@mui/icons-material';
import { couponService } from '../services/couponService';
import { campaignService } from '../services/campaignService';
import { storeService } from '../services/storeService';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { AppHeader } from '../components/AppHeader';
import { useToast } from '../contexts/ToastContext';
import { Campaign, Store, User } from '../types';
import { translateStatus, getStatusColor } from '../utils/translations';

interface CouponFormData {
  number: number;
  campaignId: string;
  storeId: string;
  sellerName?: string;
  acceptTerms: boolean;
  userId?: string;
}

const couponSchema: yup.ObjectSchema<CouponFormData> = yup.object({
  number: yup.number()
    .required('Número do cupom é obrigatório')
    .min(1, 'Número deve ser maior que zero'),
  campaignId: yup.string().required('Campanha é obrigatória'),
  storeId: yup.string().required('Loja é obrigatória'),
  sellerName: yup.string().optional(),
  acceptTerms: yup.boolean()
    .oneOf([true], 'Você deve aceitar os termos e regulamento da promoção')
    .required('Aceitação dos termos é obrigatória'),
  userId: yup.string().optional()
});

// Steps condicionais baseados no role do usuário
const getSteps = (userRole?: string) => {
  if (userRole === 'ASSOCIACAO') {
    return ['Escolher Usuário', 'Escolher Campanha', 'Selecionar Loja', 'Dados do Cupom', 'Confirmar'];
  }
  return ['Escolher Campanha', 'Selecionar Loja', 'Dados do Cupom', 'Confirmar'];
};

  const CouponFormWithUserSelection: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, logout } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [campaignStores, setCampaignStores] = useState<Store[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  const steps = getSteps(user?.role);
  const isAssociation = user?.role === 'ASSOCIACAO';

  const { control, handleSubmit, formState: { errors }, watch, setValue, trigger } = useForm<CouponFormData>({
    resolver: yupResolver(couponSchema),
    defaultValues: {
      number: 0,
      campaignId: '',
      storeId: '',
      sellerName: '',
      acceptTerms: false,
      userId: ''
    }
  });

  const watchedValues = watch();

  const fetchData = useCallback(async () => {
    try {
      setLoadingData(true);
      
      // Buscar campanhas e lojas
      const [campaignsData, storesData] = await Promise.all([
        campaignService.getActiveCampaigns(),
        storeService.getAllStores()
      ]);
      
      setCampaigns(campaignsData || []);
      setStores(storesData.stores || []);
    } catch (error: any) {
      console.error('❌ Erro ao carregar dados:', error);
      showError('Erro ao carregar campanhas e lojas. Tente novamente.');
    } finally {
      setLoadingData(false);
    }
  }, [showError]);

  // Buscar usuários se for ASSOCIACAO
  const fetchUsers = useCallback(async () => {
    if (!isAssociation) return;
    
    try {
      const usersData = await userService.searchEndUsers('', 50);
      setUsers(usersData || []);
      setFilteredUsers(usersData || []);
    } catch (error: any) {
      console.error('❌ Erro ao carregar usuários:', error);
      showError('Erro ao carregar usuários. Tente novamente.');
    }
  }, [isAssociation, showError]);

  useEffect(() => {
    fetchData();
    fetchUsers();
  }, [fetchData, fetchUsers]);

  // Filtrar usuários com base na pesquisa
  useEffect(() => {
    if (!isAssociation) return;
    
    if (userSearchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(u =>
        u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        (u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ?? false) ||
        u.cpf.includes(userSearchTerm)
      );
      setFilteredUsers(filtered);
    }
  }, [userSearchTerm, users, isAssociation]);

  // Atualizar campanha selecionada
  useEffect(() => {
    if (watchedValues.campaignId) {
      const campaign = campaigns.find(c => c.id === watchedValues.campaignId);
      setSelectedCampaign(campaign || null);
      
      // Filtrar lojas da campanha selecionada
      if (campaign?.stores) {
        setCampaignStores(campaign.stores);
      } else {
        setCampaignStores(stores);
      }
      
      // Reset store selection when campaign changes
      setValue('storeId', '');
      setSelectedStore(null);
    }
  }, [watchedValues.campaignId, campaigns, stores, setValue]);

  // Atualizar loja selecionada
  useEffect(() => {
    if (watchedValues.storeId) {
      const store = campaignStores.find(s => s.id === watchedValues.storeId);
      setSelectedStore(store || null);
    }
  }, [watchedValues.storeId, campaignStores]);

  // Atualizar usuário selecionado — busca na lista completa (não na filtrada
  // pela busca), senão digitar um termo que não bate mais com o usuário já
  // escolhido descarta a seleção silenciosamente.
  useEffect(() => {
    if (watchedValues.userId) {
      const user = users.find(u => u.id === watchedValues.userId);
      setSelectedUser(user || null);
    }
  }, [watchedValues.userId, users]);

  const handleNext = async () => {
    let fieldsToValidate: (keyof CouponFormData)[] = [];
    
    if (isAssociation) {
      switch (activeStep) {
        case 0:
          fieldsToValidate = ['userId'];
          break;
        case 1:
          fieldsToValidate = ['campaignId'];
          break;
        case 2:
          fieldsToValidate = ['storeId'];
          break;
        case 3:
          fieldsToValidate = ['number'];
          break;
        case 4:
          fieldsToValidate = ['acceptTerms'];
          break;
      }
    } else {
      switch (activeStep) {
        case 0:
          fieldsToValidate = ['campaignId'];
          break;
        case 1:
          fieldsToValidate = ['storeId'];
          break;
        case 2:
          fieldsToValidate = ['number'];
          break;
        case 3:
          fieldsToValidate = ['acceptTerms'];
          break;
      }
    }
    
    const isStepValid = await trigger(fieldsToValidate);
    if (isStepValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit: SubmitHandler<CouponFormData> = async (data) => {
    try {
      setLoading(true);

      const couponData: any = {
        number: data.number,
        campaignId: data.campaignId,
        storeId: data.storeId,
        sellerName: data.sellerName || undefined
      };

      // Se for ASSOCIACAO e selecionou um usuário, adicionar userId
      if (isAssociation && data.userId) {
        couponData.userId = data.userId;
      }

      await couponService.createCoupon(couponData);

      showSuccess('Cupom cadastrado com sucesso! 🎉');
      navigate(isAssociation ? '/coupons' : '/user/my-coupons');
    } catch (err: any) {
      console.error('❌ Erro ao salvar cupom:', err);
      
      if (err.response?.status === 409) {
        showError('Este número de cupom já foi cadastrado. Use um número diferente.');
      } else if (err.response?.status === 400) {
        // Mensagens específicas do backend para campanha pausada ou encerrada
        const errorMessage = err.response.data.message || 'Dados inválidos fornecidos';
        showError(errorMessage);
      } else if (err.response?.status === 404) {
        showError('Campanha, loja ou usuário não encontrado');
      } else if (err.response?.status === 403) {
        showError('Você não tem permissão para cadastrar cupons nesta campanha');
      } else {
        showError('Erro ao cadastrar cupom. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const isStepOptional = (step: number) => {
    return false; // Nenhum passo é opcional por enquanto
  };

  const renderStepContent = (step: number) => {
    // Ajustar índices baseado no role
    const actualStep = isAssociation ? step : step + 1;
    
    switch (actualStep) {
      case 0: // Seleção de usuário (apenas ASSOCIACAO)
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 1, color: 'primary.main' }} />
              Escolher Usuário Final
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Selecione o usuário final para quem este cupom será cadastrado
            </Typography>

            {/* Campo de Pesquisa */}
            <TextField
              fullWidth
              placeholder="Pesquisar usuário por nome, email ou CPF..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Contador de resultados */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredUsers.length === users.length 
                ? `${users.length} usuários cadastrados` 
                : `${filteredUsers.length} de ${users.length} usuários encontrados`}
            </Typography>
            
            {users.length === 0 ? (
              <Alert severity="info">
                Nenhum usuário final encontrado. Cadastre usuários primeiro.
              </Alert>
            ) : filteredUsers.length === 0 ? (
              <Alert severity="info">
                Nenhum usuário encontrado com os termos pesquisados. Tente outro termo.
              </Alert>
            ) : (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, 
                gap: 2,
                maxHeight: '400px',
                overflowY: 'auto',
                pr: 1
              }}>
                {filteredUsers.map((user) => (
                  <Box key={user.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: watchedValues.userId === user.id ? 2 : 1,
                        borderColor: watchedValues.userId === user.id 
                          ? 'primary.main' 
                          : 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2
                        },
                        height: '100%'
                      }}
                      onClick={() => setValue('userId', user.id)}
                    >
                      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flex: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                              {user.name}
                            </Typography>
                            {user.email && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                                📧 {user.email}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                              📋 {user.cpf}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              📍 {user.city}, {user.state}
                            </Typography>
                          </Box>
                          {watchedValues.userId === user.id && (
                            <CheckCircle color="primary" sx={{ ml: 1, flexShrink: 0 }} />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );

      case 1: // Campanhas
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
              Escolha uma Campanha
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Selecione a campanha promocional que você deseja participar
            </Typography>
            
            {campaigns.length === 0 ? (
              <Alert severity="info">
                Nenhuma campanha ativa encontrada no momento. Entre em contato com a associação.
              </Alert>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                {campaigns.map((campaign) => (
                  <Box key={campaign.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: watchedValues.campaignId === campaign.id ? 2 : 1,
                        borderColor: watchedValues.campaignId === campaign.id 
                          ? 'primary.main' 
                          : 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => setValue('campaignId', campaign.id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {campaign.name}
                            </Typography>
                            {campaign.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {campaign.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip 
                                size="small" 
                                label={`${formatDate(campaign.startDate)} - ${formatDate(campaign.endDate)}`}
                                icon={<Info />}
                              />
                              <Chip 
                                size="small" 
                                label={translateStatus(campaign.status || 'ACTIVE')}
                                color={getStatusColor(campaign.status || 'ACTIVE')}
                              />
                            </Box>
                          </Box>
                          {watchedValues.campaignId === campaign.id && (
                            <CheckCircle color="primary" sx={{ ml: 2 }} />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );

      case 2: // Lojas
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <StoreIcon sx={{ mr: 1, color: 'primary.main' }} />
              Selecione a Loja
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Escolha a loja onde você fez a compra que dá direito ao cupom
            </Typography>

            {selectedCampaign && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Campanha selecionada:</strong> {selectedCampaign.name}
              </Alert>
            )}
            
            {campaignStores.length === 0 ? (
              <Alert severity="warning">
                Nenhuma loja participante encontrada para esta campanha.
              </Alert>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                {campaignStores.map((store) => (
                  <Box key={store.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: watchedValues.storeId === store.id ? 2 : 1,
                        borderColor: watchedValues.storeId === store.id 
                          ? 'primary.main' 
                          : 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => setValue('storeId', store.id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {store.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              📍 {store.address}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              📞 {store.phone}
                            </Typography>
                          </Box>
                          {watchedValues.storeId === store.id && (
                            <CheckCircle color="primary" sx={{ ml: 2 }} />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );

      case 3: // Dados do cupom
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CouponIcon sx={{ mr: 1, color: 'primary.main' }} />
              Dados do Cupom
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Informe os dados do cupom de participação
            </Typography>

            <Stack spacing={3}>
              <Controller
                name="number"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Número do Cupom"
                    type="number"
                    error={!!errors.number}
                    helperText={errors.number?.message || 'Digite o número do cupom que está na nota fiscal'}
                    inputProps={{ min: 1 }}
                    size="medium"
                    required
                  />
                )}
              />

              <Controller
                name="sellerName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nome do Vendedor"
                    placeholder="Digite o nome do vendedor que atendeu você"
                    helperText="Campo opcional para identificar o vendedor"
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
        );

      case 4: // Confirmação
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Policy sx={{ mr: 1, color: 'primary.main' }} />
              Confirmação e Termos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Revise seus dados e aceite os termos da promoção
            </Typography>

            {/* Resumo dos dados */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                📋 Resumo do Cadastro
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                {isAssociation && selectedUser && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Usuário:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedUser.name}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Campanha:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedCampaign?.name || 'Não selecionada'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Loja:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedStore?.name || 'Não selecionada'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Número do Cupom:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {watchedValues.number || 'Não informado'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Vendedor:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {watchedValues.sellerName || 'Não informado'}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Regulamento da campanha */}
            {selectedCampaign?.regulation && (
              <Paper variant="outlined" sx={{ p: 3, mb: 3, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  📜 Regulamento da Campanha
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {selectedCampaign.regulation}
                </Typography>
              </Paper>
            )}

            {/* Checkbox de aceite */}
            <Paper variant="outlined" sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
              <Controller
                name="acceptTerms"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        color="primary"
                        size="large"
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        ✅ Eu li e aceito os termos e regulamento da promoção e confirmo que todas as informações fornecidas são verdadeiras.
                        {selectedCampaign && (
                          <>
                            {' '}
                            <Link
                              href={`/campaigns/${selectedCampaign.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontWeight: 600 }}
                            >
                              Ver regulamento completo
                            </Link>
                          </>
                        )}
                      </Typography>
                    }
                  />
                )}
              />
              
              {errors.acceptTerms && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.acceptTerms.message}
                </Alert>
              )}
            </Paper>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Carregando dados...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <AppHeader title="Cadastrar Cupom" subtitle="Cadastre um cupom para um usuário específico" icon={<CouponIcon />} backTo="/coupons" />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              🎯 Cadastro de Cupom
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Participe das campanhas promocionais e concorra a prêmios incríveis!
            </Typography>
            
            {/* Informação destacada de quem está cadastrando */}
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                borderRadius: 2,
                maxWidth: 400,
                mx: 'auto'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Person sx={{ color: 'success.main' }} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {isAssociation ? 'Cadastrando cupom como:' : 'Cadastrando cupom para:'}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5 }}>
                {isAssociation ? 'ASSOCIAÇÃO' : user?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAssociation 
                  ? 'Você pode escolher o usuário final destinatário' 
                  : 'Este cupom será automaticamente vinculado à sua conta'
                }
              </Typography>
            </Paper>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: { optional?: React.ReactNode } = {};
              
              if (isStepOptional(index)) {
                labelProps.optional = (
                  <Typography variant="caption">Opcional</Typography>
                );
              }
              
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* Step Content */}
          <Box sx={{ minHeight: '400px', mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="outlined"
            >
              Voltar
            </Button>
            
            <Box sx={{ flex: '1 1 auto' }} />
            
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                disabled={loading || !watchedValues.acceptTerms}
                size="large"
                sx={{ fontWeight: 600 }}
              >
                {loading ? 'Cadastrando...' : '🎉 Finalizar Cadastro'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  ((isAssociation && activeStep === 0) && !watchedValues.userId) ||
                  (((!isAssociation && activeStep === 0) || (isAssociation && activeStep === 1)) && !watchedValues.campaignId) ||
                  (((!isAssociation && activeStep === 1) || (isAssociation && activeStep === 2)) && !watchedValues.storeId) ||
                  (((!isAssociation && activeStep === 2) || (isAssociation && activeStep === 3)) && !watchedValues.number)
                }
                size="large"
                sx={{ fontWeight: 600 }}
              >
                Próximo
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </>
  );
}; 
export default CouponFormWithUserSelection;